import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Loader2, Stethoscope, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  intelligenceApi,
  type DoctorIntelligenceSortBy,
  type IntelligencePeriodPreset,
} from "@/api/intelligence";
import { superAdminApi } from "@/api/superAdmin";
import { defaultFinancialReportHospitalId } from "@/constants/pnlDefaultScope";
import { intelligenceReportHospitalId } from "@/hooks/useChainReportHospitalScope";
import {
  CommandCentreFilters,
  type IntelligencePeriodFilterValue,
} from "@/components/intelligence/CommandCentreFilters";
import { IntelligenceSubNav } from "@/components/intelligence/IntelligenceSubNav";
import { IntelligenceServerExportButton } from "@/components/intelligence/IntelligenceServerExportButton";
import { IntelligenceListPagination } from "@/components/intelligence/IntelligenceListPagination";
import { DoctorPerformanceTable } from "@/components/intelligence/DoctorPerformanceTable";
import { DoctorDetailPanel } from "@/components/intelligence/DoctorDetailPanel";
import { Card, CardContent } from "@/components/ui/card";
import { formatInr } from "@/lib/intelligenceFormat";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export default function DoctorIntelligencePage() {
  const { user, activeHospitalId, isChainAdmin } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const isInvestor = user?.role === "INVESTOR";
  const investorHasChainScope = isInvestor && (user?.hospitals?.length ?? 0) > 1;
  const showHospitalFilter = isSuperAdmin || isChainAdmin || investorHasChainScope;
  const chainHospitals = user?.hospitals ?? [];
  const isChainScoped = isChainAdmin || investorHasChainScope;

  const defaultFinancialHospitalId = useMemo(
    () =>
      defaultFinancialReportHospitalId({
        isSuperAdmin,
        isChainScoped,
        activeHospitalId,
        fallbackHospitalId: chainHospitals[0]?.id ?? null,
      }),
    [isSuperAdmin, isChainScoped, activeHospitalId, chainHospitals]
  );

  const [period, setPeriod] = useState<IntelligencePeriodFilterValue>({ preset: "this_month" });
  const [selectedHospitalId, setSelectedHospitalId] = useState("");
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<DoctorIntelligenceSortBy>("revenue");
  const pageSize = 25;

  useEffect(() => {
    if (!selectedHospitalId && defaultFinancialHospitalId !== undefined) {
      setSelectedHospitalId(defaultFinancialHospitalId || "all");
    }
  }, [defaultFinancialHospitalId, selectedHospitalId]);

  const { data: superHospitals } = useQuery({
    queryKey: ["super-admin-hospitals-doctors"],
    queryFn: () => superAdminApi.getHospitals(),
    enabled: isSuperAdmin,
  });

  const hospitalOptions = useMemo(() => {
    if (isSuperAdmin && superHospitals?.length) {
      return superHospitals.map((h) => ({ id: h.id, name: h.name }));
    }
    if (isChainScoped && chainHospitals.length) {
      return chainHospitals.map((h) => ({ id: h.id, name: h.name }));
    }
    if (user?.hospitalId) {
      const name = chainHospitals.find((h) => h.id === user.hospitalId)?.name ?? "My Hospital";
      return [{ id: user.hospitalId, name }];
    }
    return [];
  }, [isSuperAdmin, isChainScoped, superHospitals, chainHospitals, user?.hospitalId]);

  const reportHospitalId = useMemo(
    () =>
      intelligenceReportHospitalId({
        showHospitalFilter,
        selectedHospitalId,
        isChainScoped,
      }),
    [showHospitalFilter, selectedHospitalId, isChainScoped]
  );

  const customReady =
    period.preset !== "custom" || (!!period.from && !!period.to && period.from <= period.to);

  const queryParams = {
    period: period.preset as IntelligencePeriodPreset,
    from: period.preset === "custom" ? period.from : undefined,
    to: period.preset === "custom" ? period.to : undefined,
    hospitalId: reportHospitalId,
    compare: true,
    page,
    pageSize,
    sortBy,
  };

  useEffect(() => {
    setPage(1);
    setSelectedDoctorId(null);
  }, [reportHospitalId, period.preset, period.from, period.to, sortBy]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["intelligence-doctors", queryParams],
    queryFn: () => intelligenceApi.getDoctors(queryParams),
    enabled: customReady,
  });

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ["intelligence-doctor-detail", selectedDoctorId, queryParams],
    queryFn: () => intelligenceApi.getDoctorDetail(selectedDoctorId!, queryParams),
    enabled: customReady && !!selectedDoctorId,
  });

  return (
    <div className="space-y-6 pb-10">
      <IntelligenceSubNav
        basePath={
          isSuperAdmin ? "/super-admin" : "/hospital-admin"
        }
      />

      <div>
        <div className="flex items-center gap-2 text-indigo-700">
          <Stethoscope className="h-6 w-6" />
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Doctor Intelligence</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Doctor-level revenue and volume · {data?.period.label ?? "—"}
        </p>
      </div>

      <CommandCentreFilters
        hospitals={hospitalOptions}
        showHospitalFilter={showHospitalFilter}
        hospitalId={selectedHospitalId}
        onHospitalChange={setSelectedHospitalId}
        period={period}
        onPeriodChange={setPeriod}
      />

      {data && customReady && (
        <div className="flex justify-end">
          <IntelligenceServerExportButton
            report="doctors"
            params={queryParams}
            filename={`doctor-intelligence-${data.period.label.replace(/[^\w-]+/g, "-").toLowerCase()}`}
          />
        </div>
      )}

      {isLoading && customReady && (
        <div className="flex justify-center py-16 text-muted-foreground gap-2">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading…
        </div>
      )}

      {isError && customReady && (
        <Card className="border-destructive/40">
          <CardContent className="pt-4 text-sm text-destructive flex gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {(error as Error)?.message ?? "Failed to load doctor intelligence"}
          </CardContent>
        </Card>
      )}

      {data && customReady && (
        <>
          <div className="flex flex-wrap gap-4 text-sm items-center justify-between">
            <div className="flex flex-wrap gap-4">
              <span className="text-muted-foreground">
                Total attributed revenue:{" "}
                <strong className="text-foreground tabular-nums">{formatInr(data.totalRevenue)}</strong>
              </span>
              <span className="text-muted-foreground">
                {data.pagination?.total ?? data.doctors.length} doctors with activity
              </span>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="doctor-sort" className="text-muted-foreground">
                Sort by
              </label>
              <select
                id="doctor-sort"
                className="border rounded-md px-3 py-1.5 text-sm"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as DoctorIntelligenceSortBy)}
              >
                <option value="revenue">Revenue</option>
                <option value="patients">Patients</option>
                <option value="name">Name</option>
              </select>
            </div>
          </div>

          {data.limitations[0] && (
            <p className="text-xs text-muted-foreground">{data.limitations[0]}</p>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="space-y-3">
              <DoctorPerformanceTable
                doctors={data.doctors}
                selectedDoctorId={selectedDoctorId}
                onSelectDoctor={setSelectedDoctorId}
                serverSorted
              />
              {data.pagination && (
                <IntelligenceListPagination
                  page={data.pagination.page}
                  pageSize={data.pagination.pageSize}
                  total={data.pagination.total}
                  onPageChange={setPage}
                />
              )}
            </div>
            <div>
              {selectedDoctorId && detailLoading && (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}
              {selectedDoctorId && detail && <DoctorDetailPanel detail={detail} />}
              {!selectedDoctorId && (
                <Card className="h-full min-h-[200px] flex items-center justify-center">
                  <CardContent className="text-sm text-muted-foreground text-center py-12">
                    Select a doctor to view detail, trends, and service mix
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
